import {
  getSessionCookie,
  updateSessionCookie,
} from "@prmf/cloud-utils/cookies.server";
import cache from "@prmf/visualizer-cache";
import TopNav from "@prmf/visualizer-components/src/TopNav";
import {
  ActionFunctionArgs,
  HeadersFunction,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  ClientActionFunction,
  ClientLoaderFunction,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  json,
  matchPath,
  useLocation,
} from "@remix-run/react";
import { FieldValue, getFirestore } from "firebase-admin/firestore";
import { Toaster } from "react-hot-toast";
import "./index.css";

export const headers: HeadersFunction = () => ({
  //https://firebase.google.com/docs/hosting/manage-cache
  "Cache-Control": "public, max-age=86400, s-max-age=86400",
});

export const links: LinksFunction = () => [
  { rel: "shortcut icon", href: "#", type: "image/x-icon" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap",
  },
];

export const meta: MetaFunction = () => {
  return [
    { property: "og:type", content: "website" },
    { property: "og:title", content: "PRMF Kitchen Visualiser" },
    { property: "og:url", content: "https://visualizer.prmf.co.uk" },
    {
      property: "og:image",
      content: "https://visualizer.prmf.co.uk/ogimage.png",
    },
    {
      property: "og:description",
      content: "A tool to help you creating your dream kitchen",
    },
  ];
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const cookie = await getSessionCookie(request);
  let vizId = cookie?.vizId as string | undefined;
  let email = cookie?.email as string | undefined;
  const path = new URL(request.url).pathname;
  const db = getFirestore().collection("visualizer");
  let bookmarks: string[];

  if (vizId && path === "/") {
    return redirect("/c");
  }

  if (vizId) {
    const viz = await db.doc(vizId).get();
    email = viz.get("email");
    bookmarks = viz.get("items") || [];
    return json(
      { email, bookmarks },
      {
        headers: {
          "Set-Cookie": await updateSessionCookie(request, { email }),
        },
      },
    );
  }

  if (!email) {
    email = new URL(request.url).searchParams.get("email")?.toString();
  }

  if (email) {
    const ref = db.where("email", "==", email).limit(1).select("items");
    const viz = (await ref.get()).docs.at(0);
    const bookmarks = viz?.get("items") || [];

    if (!viz?.id) {
      const ref = db.doc();
      await ref.create({
        createdAt: FieldValue.serverTimestamp(),
        email,
        items: [],
      });
      vizId = ref.id;
    }
    const headers = {
      "Set-Cookie": await updateSessionCookie(request, { email, vizId }),
    };

    if (path === "/") {
      return redirect("/c", { headers });
    }

    return json({ email, bookmarks }, { headers });
  }

  return { email, bookmarks: [] };
};

export const clientLoader: ClientLoaderFunction = async ({ serverLoader }) => {
  const data = await serverLoader<typeof loader>();
  if (data.email) {
    cache.set("email", data.email, true);
  }
  if (cache.get("email")) return data;

  return { bookmarks: cache.get("bookmarks") };
};
clientLoader.hydrate = true;

export const action = async ({ request }: ActionFunctionArgs) => {
  if (request.method === "POST") {
    let formData: FormData;
    try {
      formData = await request.formData();
    } catch {
      return new Response("Invalid form submission", { status: 400 });
    }

    const email = formData.get("email")?.toString();
    return redirect("/c", {
      headers: {
        "Set-Cookie": await updateSessionCookie(request, { email }),
      },
    });
  }
  if (request.method === "DELETE") {
    return redirect("/", {
      headers: {
        "Set-Cookie": await updateSessionCookie(request, {
          auth: undefined,
          email: undefined,
          vizId: undefined,
          profileId: undefined,
        }),
      },
    });
  }
  return null;
};

export const clientAction: ClientActionFunction = async ({
  request,
  serverAction,
}) => {
  const req = request.clone();
  if (req.method === "POST") {
    try {
      const email = (await req.formData()).get("email")?.toString();
      cache.set("email", email, true);
    } catch {
      // Ignore malformed requests here; the server action handles them too.
    }
  }
  if (req.method === "DELETE") {
    cache.set("email", null, true);
  }
  return serverAction();
};

export default function App() {
  const { pathname } = useLocation();
  const isSummaryPrintView = matchPath("/p/*", pathname);
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <ScrollRestoration />
        {isSummaryPrintView ? (
          <Outlet />
        ) : (
          <div className="flex min-h-screen flex-col bg-gray-600">
            <Toaster containerClassName="text-sm" />
            <TopNav />
            <div className="flex flex-1">
              <Outlet />
            </div>
          </div>
        )}
        <Scripts />
      </body>
    </html>
  );
}
